import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { LocalVideoTrack, Track, type Room } from 'livekit-client';

/**
 * MG1: Meta-glasögonens kamera i jaktmötet. Den nativa delen (ios/App/App/MetaGlassesPlugin.swift)
 * finns bara i glasögonvarianten av iOS-appen (målet AppGlasses, intern TestFlight) och skickar
 * glasögonens bildrutor som JPEG. Här ritas de på en canvas vars ström publiceras som jägarens
 * kamera (Track.Source.Camera), så de andra i jaktlaget ser den som vilken kamera som helst.
 * Se docs/utredning-meta-glasogon.md.
 */

export type GlassesRegistration = 'unavailable' | 'available' | 'registering' | 'registered' | 'unknown';

export interface GlassesStateEvent {
  state: 'permission' | 'connecting' | 'waiting' | 'starting' | 'streaming' | 'paused' | 'stopping' | 'stopped' | 'error';
  message?: string;
  /** DAT:s rå status (registrering, länk, bärs, gångjärn, session, fel) för felrapporter. */
  diag?: string;
}

interface GlassesFrame { jpeg: string; width: number; height: number }

interface MetaGlassesPlugin {
  status(): Promise<{ available: boolean; registration: GlassesRegistration; devices: number; streaming: boolean; mock: boolean; diag?: string }>;
  connect(): Promise<{ registration: GlassesRegistration }>;
  start(options: { resolution?: 'low' | 'medium' | 'high'; fps?: number; quality?: number }): Promise<{ state: string }>;
  stop(): Promise<void>;
  enableMock(options: { videoPath?: string }): Promise<{ registration: GlassesRegistration }>;
  addListener(event: 'frame', cb: (f: GlassesFrame) => void): Promise<PluginListenerHandle>;
  addListener(event: 'state', cb: (e: GlassesStateEvent) => void): Promise<PluginListenerHandle>;
}

export const MetaGlasses = registerPlugin<MetaGlassesPlugin>('MetaGlasses');

export const GLASSES_TRACK_NAME = 'glasogon';
const FPS = 15;

export function isMetaGlassesAvailable(): boolean {
  const cap = (window as any).Capacitor;
  // Webben är densamma för alla appbyggen; bara glasögonvarianten har pluginet.
  return !!cap?.isNativePlatform?.() && cap.getPlatform?.() === 'ios' && !!cap.isPluginAvailable?.('MetaGlasses');
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Väntar tills Qvenari är kopplad i Meta AI-appen (jägaren godkänner där och kommer tillbaka). */
async function ensureRegistered(onStatus: (msg: string) => void): Promise<void> {
  let { registration } = await MetaGlasses.status();
  if (registration === 'registered') return;
  onStatus('Godkänn Qvenari i Meta AI-appen och kom sedan tillbaka hit');
  await MetaGlasses.connect();
  for (let i = 0; i < 120 && registration !== 'registered'; i++) {
    await sleep(1000);
    ({ registration } = await MetaGlasses.status());
  }
  if (registration !== 'registered') {
    throw new Error('Qvenari blev inte kopplad till glasögonen. Är Developer Mode påslaget i Meta AI-appen?');
  }
}

/** Ritar inkommande JPEG-rutor på en canvas; den senaste vinner om avkodningen hamnar efter. */
export class GlassesCanvas {
  readonly canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private busy = false;
  private pending: GlassesFrame | null = null;
  frames = 0;

  constructor(doc: Document = document) {
    this.canvas = doc.createElement('canvas');
    this.canvas.width = 360;
    this.canvas.height = 640;
    this.ctx = this.canvas.getContext('2d')!;
  }

  push(frame: GlassesFrame): Promise<void> {
    if (this.busy) { this.pending = frame; return Promise.resolve(); }
    return this.draw(frame);
  }

  private async draw(frame: GlassesFrame): Promise<void> {
    this.busy = true;
    try {
      const img = new Image();
      img.src = `data:image/jpeg;base64,${frame.jpeg}`;
      await img.decode();
      const w = frame.width || img.naturalWidth;
      const h = frame.height || img.naturalHeight;
      if (this.canvas.width !== w || this.canvas.height !== h) { this.canvas.width = w; this.canvas.height = h; }
      this.ctx.drawImage(img, 0, 0, w, h);
      this.frames++;
    } catch { /* trasig ruta: hoppa över */ }
    this.busy = false;
    const next = this.pending;
    this.pending = null;
    if (next) await this.draw(next);
  }
}

/** En pågående delning: glasögonens bild publicerad som jägarens kamera i rummet. */
export class GlassesShare {
  private handles: PluginListenerHandle[] = [];
  private track: LocalVideoTrack | null = null;
  private room: Room | null = null;
  private stopped = false;
  private readonly view: GlassesCanvas;

  constructor(private readonly onState: (e: GlassesStateEvent) => void, doc: Document = document) {
    this.view = new GlassesCanvas(doc);
  }

  /** Tidigaste bilden ser jägaren själv i förhandsvisningen. */
  get localTrack(): LocalVideoTrack | null { return this.track; }

  async start(room: Room): Promise<void> {
    this.room = room;
    this.handles.push(await MetaGlasses.addListener('frame', (f) => { void this.view.push(f); }));
    this.handles.push(await MetaGlasses.addListener('state', (e) => {
      // Under uppstarten försöker den nativa delen själv igen; bara en publicerad delning avslutas här.
      if (e.state === 'stopped' && this.track && !this.stopped) { void this.stop(); }
      this.onState(e);
    }));
    try {
      await ensureRegistered((message) => this.onState({ state: 'permission', message }));
      await MetaGlasses.start({ resolution: 'medium', fps: FPS, quality: 0.5 });
      // Vänta på första bilden innan spåret publiceras, annars ser laget en svart ruta.
      for (let i = 0; i < 600 && this.view.frames === 0 && !this.stopped; i++) await sleep(100);
      if (this.stopped) return;
      if (this.view.frames === 0) throw new Error('Ingen bild från glasögonen. Har du dem på dig och är de uppfällda?');
      const mediaTrack = this.view.canvas.captureStream(FPS).getVideoTracks()[0];
      if (!mediaTrack) throw new Error('Kunde inte skapa videospåret');
      this.track = new LocalVideoTrack(mediaTrack, undefined, false);
      await room.localParticipant.publishTrack(this.track, {
        source: Track.Source.Camera,
        name: GLASSES_TRACK_NAME,
        simulcast: false,
      });
      this.onState({ state: 'streaming' });
    } catch (err: any) {
      // Capacitor lägger nativa reject-data i err.data.
      const diag = err?.data?.diag;
      await this.stop();
      if (diag && err instanceof Error) (err as Error & { diag?: string }).diag = diag;
      throw err;
    }
  }

  async stop(): Promise<void> {
    if (this.stopped) return;
    this.stopped = true;
    const handles = this.handles.splice(0);
    await Promise.all(handles.map((h) => h.remove().catch(() => {})));
    if (this.track) {
      try { await this.room?.localParticipant.unpublishTrack(this.track, true); } catch { /* rummet kan redan vara stängt */ }
      this.track.stop();
      this.track = null;
    }
    await MetaGlasses.stop().catch(() => {});
    this.onState({ state: 'stopped' });
  }
}
